/**
 * Scheduling Manhour Estimator
 * Helps PMs estimate required scheduling manhours using two methods:
 * 1. Quick Estimate (Rule of Thumb) - based on project value
 * 2. Detailed Estimate - based on complexity factors
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { ListenButton } from '../common/AudioGuide';
import HelpVideoTour from '../Help/HelpVideoTour';
import { hasHelpTour } from '../Help/HelpWithVideoTour';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

// ─── Constants ───────────────────────────────────────────────────────────────

const SITE_PRESENCE_TIERS = [
  { maxValue: 10, days: 1, label: '1 day/week', color: '#10b981' },
  { maxValue: 20, days: 3, label: '3 days/week', color: '#f59e0b' },
  { maxValue: 30, days: 4, label: '4 days/week', color: '#f97316' },
  { maxValue: 40, days: 5, label: '5 days/week', color: '#ef4444' },
  { maxValue: Infinity, days: 6, label: '6 days/week (full-time)', color: '#dc2626' },
];

const MINIMUM_HOURS_TIERS = [
  { maxValue: 10, minHours: 8 },
  { maxValue: 20, minHours: 20 },
  { maxValue: 30, minHours: 35 },
  { maxValue: 40, minHours: 50 },
  { maxValue: Infinity, minHours: 50 },
];

const COMPLEXITY_OPTIONS = [
  { value: 'low', label: 'Low', multiplier: 0.8, description: 'Repetitive work, single trade, simple scope' },
  { value: 'medium', label: 'Medium', multiplier: 1.0, description: 'Standard commercial/industrial project' },
  { value: 'high', label: 'High', multiplier: 1.3, description: 'Multi-phase, tight sequencing, congested site' },
  { value: 'veryHigh', label: 'Very High', multiplier: 1.6, description: 'Turnaround, shutdown, or critical path intensive' },
];

const HOURS_PER_MILLION = 1.5;

// ─── Utility Functions ──────────────────────────────────────────────────────

function getSitePresenceTier(valueM) {
  return SITE_PRESENCE_TIERS.find(t => valueM < t.maxValue) || SITE_PRESENCE_TIERS[SITE_PRESENCE_TIERS.length - 1];
}

function getMinimumHours(valueM) {
  const tier = MINIMUM_HOURS_TIERS.find(t => valueM < t.maxValue) || MINIMUM_HOURS_TIERS[MINIMUM_HOURS_TIERS.length - 1];
  return tier.minHours;
}

function applyMinimum(calculated, valueM) {
  const min = getMinimumHours(valueM);
  return Math.max(calculated, min);
}

function formatNumber(n, decimals = 1) {
  if (n == null || isNaN(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

// ─── Help Modal ─────────────────────────────────────────────────────────────

const HelpModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('quick');
  if (!isOpen) return null;

  const tabs = [
    { id: 'quick', label: 'Quick Estimate' },
    { id: 'detailed', label: 'Detailed Estimate' },
    { id: 'presence', label: 'Site Presence' },
    { id: 'references', label: 'References' },
    ...(hasHelpTour('scheduling-estimator-help') ? [{ id: 'tour', label: 'Video Tour' }] : []),
  ];

  return (
    <div onClick={onClose} style={styles.modalOverlay}>
      <div onClick={e => e.stopPropagation()} style={styles.modalContent}>
        <div style={styles.modalHeader}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>Scheduling Estimation Guide</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton compact guideId="scheduling-estimator-help" title="Scheduling Estimator Help" accent="#dc2626" />
            <button onClick={onClose} style={styles.modalClose}>&times;</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '4px', padding: '0 24px', borderBottom: '1px solid #e5e7eb' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: activeTab === tab.id ? '600' : '400',
                color: activeTab === tab.id ? '#dc2626' : '#6b7280',
                borderBottom: activeTab === tab.id ? '2px solid #dc2626' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >{tab.label}</button>
          ))}
        </div>

        <div style={{ padding: '24px', overflowY: 'auto', maxHeight: '55vh', lineHeight: 1.7 }}>
          {activeTab === 'tour' && (
            <HelpVideoTour guideId="scheduling-estimator-help" title="Scheduling Estimator Help" accent="#dc2626" compact showHeader />
          )}
          {activeTab === 'quick' && (
            <div>
              <h3 style={styles.helpH3}>Rule of Thumb Method</h3>
              <p>A fast, industry-standard approximation based on total project value:</p>
              <div style={styles.formulaBox}>
                <strong>Weekly Hours = Project Value ($M) x {HOURS_PER_MILLION} hrs/week</strong>
                <div style={{ fontSize: '0.8rem', marginTop: '6px', color: '#b91c1c' }}>subject to minimum hours per tier</div>
              </div>
              <p>For example, a $15M project calculates to 22.5 hrs/week but the minimum for projects under $20M is <strong>20 hrs/week</strong>, so <strong>22.5 hrs/week</strong> applies.</p>
              <h4 style={{ marginTop: '16px', color: '#374151' }}>Minimum Weekly Hours</h4>
              <table style={styles.helpTable}>
                <thead>
                  <tr>
                    <th style={styles.helpTh}>Project Value</th>
                    <th style={styles.helpTh}>Calculated</th>
                    <th style={styles.helpTh}>Minimum</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td style={styles.helpTd}>&lt; $10M</td><td style={styles.helpTd}>Value x 1.5</td><td style={styles.helpTd}><strong>8 hrs/week</strong></td></tr>
                  <tr><td style={styles.helpTd}>&lt; $20M</td><td style={styles.helpTd}>Value x 1.5</td><td style={styles.helpTd}><strong>20 hrs/week</strong></td></tr>
                  <tr><td style={styles.helpTd}>&lt; $30M</td><td style={styles.helpTd}>Value x 1.5</td><td style={styles.helpTd}><strong>35 hrs/week</strong></td></tr>
                  <tr><td style={styles.helpTd}>&lt; $40M</td><td style={styles.helpTd}>Value x 1.5</td><td style={styles.helpTd}><strong>50 hrs/week</strong></td></tr>
                  <tr><td style={styles.helpTd}>&ge; $40M</td><td style={styles.helpTd}>Value x 1.5</td><td style={styles.helpTd}><strong>50 hrs/week</strong></td></tr>
                </tbody>
              </table>
              <p style={{ marginTop: '12px' }}>This covers schedule development, updates, progress tracking, reporting, and coordination meetings.</p>
              <h4 style={{ marginTop: '16px', color: '#374151' }}>When to Use</h4>
              <ul style={{ paddingLeft: '20px', color: '#4b5563' }}>
                <li>Early-stage feasibility or budgeting (AACE Class 4-5 estimates)</li>
                <li>Proposals and bid preparation</li>
                <li>Quick sanity checks against detailed estimates</li>
              </ul>
            </div>
          )}

          {activeTab === 'detailed' && (
            <div>
              <h3 style={styles.helpH3}>Detailed Complexity Method</h3>
              <p>A weighted calculation that accounts for project-specific factors known to drive scheduling effort:</p>
              <table style={styles.helpTable}>
                <thead>
                  <tr>
                    <th style={styles.helpTh}>Factor</th>
                    <th style={styles.helpTh}>Impact</th>
                    <th style={styles.helpTh}>Rationale</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td style={styles.helpTd}><strong>Project Value</strong></td><td style={styles.helpTd}>Base hours at {HOURS_PER_MILLION}/week per $M</td><td style={styles.helpTd}>Larger projects need more scheduling</td></tr>
                  <tr><td style={styles.helpTd}><strong>Complexity</strong></td><td style={styles.helpTd}>0.8x to 1.6x multiplier</td><td style={styles.helpTd}>Multi-phase, tight sequencing, congested sites need more effort</td></tr>
                  <tr><td style={styles.helpTd}><strong>Areas</strong></td><td style={styles.helpTd}>+0.5 hrs/week each</td><td style={styles.helpTd}>Each area adds geographic coordination</td></tr>
                  <tr><td style={styles.helpTd}><strong>Disciplines</strong></td><td style={styles.helpTd}>+0.75 hrs/week each</td><td style={styles.helpTd}>Cross-discipline sequencing and interfaces</td></tr>
                  <tr><td style={styles.helpTd}><strong>Subcontractors</strong></td><td style={styles.helpTd}>+0.25 hrs/week each</td><td style={styles.helpTd}>External coordination, submittals, meetings</td></tr>
                  <tr><td style={styles.helpTd}><strong>Total Manhours</strong></td><td style={styles.helpTd}>Scale factor</td><td style={styles.helpTd}>Labour-intensive projects have more activities to schedule</td></tr>
                  <tr style={{ backgroundColor: '#fef2f2' }}><td style={styles.helpTd}><strong>Shutdown/TAR</strong></td><td style={styles.helpTd}>1.5x multiplier + premium</td><td style={styles.helpTd}>Compressed timelines, 6-7 day weeks, intensive coordination</td></tr>
                </tbody>
              </table>
              <div style={{ ...styles.formulaBox, marginTop: '16px', backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
                <strong>Shutdown projects</strong> apply a 1.5x global multiplier plus additional hours for coordination overhead per subcontractor and project scale.
              </div>
              <h4 style={{ marginTop: '16px', color: '#374151' }}>When to Use</h4>
              <ul style={{ paddingLeft: '20px', color: '#4b5563' }}>
                <li>Project execution planning (post-award)</li>
                <li>Resource loading and staffing plans</li>
                <li>Comparing options for different project strategies</li>
                <li>Shutdown / turnaround planning — toggle the shutdown flag for accurate estimates</li>
              </ul>
            </div>
          )}

          {activeTab === 'presence' && (
            <div>
              <h3 style={styles.helpH3}>Minimum Site Presence Guidelines</h3>
              <p>Industry-standard minimums for scheduler on-site presence based on project value:</p>
              <table style={styles.helpTable}>
                <thead>
                  <tr>
                    <th style={styles.helpTh}>Project Value</th>
                    <th style={styles.helpTh}>Minimum Presence</th>
                    <th style={styles.helpTh}>Typical Activities</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td style={styles.helpTd}>&lt; $10M</td><td style={styles.helpTd}>1 day/week</td><td style={styles.helpTd}>Progress collection, schedule update, brief report</td></tr>
                  <tr><td style={styles.helpTd}>&lt; $20M</td><td style={styles.helpTd}>3 days/week</td><td style={styles.helpTd}>+ Look-ahead meetings, constraint tracking</td></tr>
                  <tr><td style={styles.helpTd}>&lt; $30M</td><td style={styles.helpTd}>4 days/week</td><td style={styles.helpTd}>+ Daily coordination, resource leveling</td></tr>
                  <tr><td style={styles.helpTd}>&lt; $40M</td><td style={styles.helpTd}>5 days/week</td><td style={styles.helpTd}>+ Full-time presence, critical path management</td></tr>
                  <tr><td style={styles.helpTd}>&ge; $40M</td><td style={styles.helpTd}>6 days/week (full-time)</td><td style={styles.helpTd}>Dedicated scheduler with assistant, weekend coverage</td></tr>
                </tbody>
              </table>
              <p style={{ marginTop: '12px', color: '#6b7280', fontSize: '0.85rem' }}>
                These are minimums. Complex projects (shutdowns, multi-phase, union environments) often require additional presence regardless of value.
              </p>
            </div>
          )}

          {activeTab === 'references' && (
            <div>
              <h3 style={styles.helpH3}>Industry References</h3>
              <ul style={{ paddingLeft: '20px', color: '#4b5563', lineHeight: 2 }}>
                <li><strong>AACE International RP 56R-08</strong> — Cost Estimate Classification System</li>
                <li><strong>RSMeans 2026 Labour Rates</strong> — Baseline productivity rates and adjustment factors</li>
                <li><strong>PMI Practice Standard for Scheduling</strong> — Scheduling process and resource requirements</li>
                <li><strong>CIQS / CCA Standards</strong> — Canadian construction scheduling norms</li>
              </ul>
              <h4 style={{ marginTop: '16px', color: '#374151' }}>Key Concepts</h4>
              <ul style={{ paddingLeft: '20px', color: '#4b5563' }}>
                <li><strong>Productive hour:</strong> RSMeans assumes 45 productive minutes per 60-minute clock hour</li>
                <li><strong>Overtime fatigue:</strong> After 4 weeks of 50-hr weeks, productivity drops to ~85% of baseline</li>
                <li><strong>Calendar conversion:</strong> 5-day schedule uses a 1.15-1.25x factor for weekends and holidays</li>
                <li><strong>Rework allowance:</strong> 2-5% for well-managed projects, up to 15% for fast-track</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Gauge Component ────────────────────────────────────────────────────────

const SitePresenceGauge = ({ days, label, color }) => {
  const pct = (days / 6) * 100;
  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '500' }}>Minimum Site Presence</span>
        <span style={{ fontSize: '0.85rem', fontWeight: '700', color }}>{label}</span>
      </div>
      <div style={{ height: '10px', backgroundColor: '#f3f4f6', borderRadius: '5px', overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%', backgroundColor: color,
          borderRadius: '5px', transition: 'width 0.4s ease',
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
        {[1,2,3,4,5,6].map(d => (
          <span key={d} style={{ fontSize: '0.65rem', color: d <= days ? color : '#d1d5db', fontWeight: d <= days ? '600' : '400' }}>
            {d}d
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── Result Card ────────────────────────────────────────────────────────────

const ResultCard = ({ label, value, unit, subtitle, accent = '#dc2626', large }) => (
  <div style={{
    backgroundColor: '#fff', borderRadius: '12px', padding: large ? '24px' : '16px',
    border: '1px solid #e5e7eb', borderTop: `3px solid ${accent}`,
    textAlign: 'center', flex: 1, minWidth: 0,
  }}>
    <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
      {label}
    </div>
    <div style={{ fontSize: large ? '2rem' : '1.5rem', fontWeight: '800', color: '#111827', lineHeight: 1.1 }}>
      {value}
      {unit && <span style={{ fontSize: '0.7em', fontWeight: '500', color: '#6b7280', marginLeft: '4px' }}>{unit}</span>}
    </div>
    {subtitle && <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>{subtitle}</div>}
  </div>
);

// ─── Input Field ────────────────────────────────────────────────────────────

const InputField = ({ label, value, onChange, suffix, min = 0, step = 1, tooltip }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
      {label}
      {tooltip && <span style={{ marginLeft: '6px', color: '#9ca3af', fontWeight: '400', fontSize: '0.75rem' }}>({tooltip})</span>}
    </label>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <input
        type="number"
        value={value}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        min={min}
        step={step}
        style={{
          flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px',
          fontSize: '0.95rem', fontWeight: '500', color: '#111827', outline: 'none',
          transition: 'border-color 0.2s',
        }}
        onFocus={e => e.target.style.borderColor = '#dc2626'}
        onBlur={e => e.target.style.borderColor = '#d1d5db'}
      />
      {suffix && <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>{suffix}</span>}
    </div>
  </div>
);

// ─── Complexity Selector ────────────────────────────────────────────────────

const ComplexitySelector = ({ value, onChange }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
      Project Complexity
    </label>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
      {COMPLEXITY_OPTIONS.map(opt => {
        const isSelected = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            title={opt.description}
            style={{
              padding: '10px 6px', border: isSelected ? '2px solid #dc2626' : '1px solid #d1d5db',
              borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
              backgroundColor: isSelected ? '#fef2f2' : '#fff',
              transition: 'all 0.2s',
            }}
          >
            <div style={{ fontSize: '0.8rem', fontWeight: isSelected ? '700' : '500', color: isSelected ? '#dc2626' : '#374151' }}>
              {opt.label}
            </div>
            <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '2px' }}>{opt.multiplier}x</div>
          </button>
        );
      })}
    </div>
    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '6px', fontStyle: 'italic' }}>
      {COMPLEXITY_OPTIONS.find(o => o.value === value)?.description}
    </div>
  </div>
);

// ─── Breakdown Row ──────────────────────────────────────────────────────────

const BreakdownRow = ({ label, hours, pct, color = '#3b82f6' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
    <div style={{ flex: 1, fontSize: '0.8rem', color: '#374151' }}>{label}</div>
    <div style={{ width: '120px' }}>
      <div style={{ height: '6px', backgroundColor: '#f3f4f6', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', backgroundColor: color, borderRadius: '3px', transition: 'width 0.3s' }} />
      </div>
    </div>
    <div style={{ width: '70px', textAlign: 'right', fontSize: '0.8rem', fontWeight: '600', color: '#111827' }}>
      {formatNumber(hours)} hrs
    </div>
  </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────

const SchedulingEstimatorPage = () => {
  const [activeMethod, setActiveMethod] = useState('quick');
  const [showHelp, setShowHelp] = useState(false);
  const printRef = useRef(null);
  const { viewers, isConnected, connectionState } = usePresence('schedulingEstimator', 'global', true);

  // Quick Estimate inputs
  const [quickProjectValue, setQuickProjectValue] = useState(15);
  const [quickDurationWeeks, setQuickDurationWeeks] = useState(52);

  // Detailed Estimate inputs
  const [detProjectValue, setDetProjectValue] = useState(15);
  const [detDurationWeeks, setDetDurationWeeks] = useState(52);
  const [detComplexity, setDetComplexity] = useState('medium');
  const [detAreas, setDetAreas] = useState(2);
  const [detDisciplines, setDetDisciplines] = useState(4);
  const [detSubcontractors, setDetSubcontractors] = useState(5);
  const [detTotalManhours, setDetTotalManhours] = useState(100000);
  const [detIsShutdown, setDetIsShutdown] = useState(false);

  // Quick estimate calculations
  const quickResults = useMemo(() => {
    const calculated = quickProjectValue * HOURS_PER_MILLION;
    const minimum = getMinimumHours(quickProjectValue);
    const weeklyHours = applyMinimum(calculated, quickProjectValue);
    const isMinApplied = calculated < minimum;
    const totalHours = weeklyHours * quickDurationWeeks;
    const tier = getSitePresenceTier(quickProjectValue);
    return { weeklyHours, totalHours, tier, calculated, minimum, isMinApplied };
  }, [quickProjectValue, quickDurationWeeks]);

  // Detailed estimate calculations
  const detailedResults = useMemo(() => {
    const complexityOpt = COMPLEXITY_OPTIONS.find(o => o.value === detComplexity);
    const complexityMult = complexityOpt?.multiplier || 1.0;
    // Shutdowns/turnarounds: compressed timelines, 6-7 day weeks, intense coordination
    const shutdownMult = detIsShutdown ? 1.5 : 1.0;

    const baseHours = detProjectValue * HOURS_PER_MILLION;
    const areaHours = detAreas * 0.5;
    const disciplineHours = detDisciplines * 0.75;
    const subconHours = detSubcontractors * 0.25;
    // Manhour scaling: every 50k manhours adds ~0.5 hrs/week
    const manhourScale = (detTotalManhours / 50000) * 0.5;
    // Shutdown adds a flat coordination premium
    const shutdownHours = detIsShutdown ? (detProjectValue * 0.5 + detSubcontractors * 0.5) : 0;

    const rawWeekly = baseHours + areaHours + disciplineHours + subconHours + manhourScale + shutdownHours;
    const weeklyHours = rawWeekly * complexityMult * shutdownMult;
    const minimum = getMinimumHours(detProjectValue);
    const finalWeekly = Math.max(weeklyHours, minimum);
    const totalHours = finalWeekly * detDurationWeeks;
    const tier = getSitePresenceTier(detProjectValue);

    return {
      weeklyHours: finalWeekly, totalHours, tier, complexityMult, shutdownMult,
      isMinApplied: weeklyHours < minimum,
      breakdown: {
        base: { label: 'Base (project value)', hours: baseHours * complexityMult * shutdownMult, pct: (baseHours / rawWeekly) * 100, color: '#3b82f6' },
        areas: { label: `Areas (${detAreas})`, hours: areaHours * complexityMult * shutdownMult, pct: (areaHours / rawWeekly) * 100, color: '#8b5cf6' },
        disciplines: { label: `Disciplines (${detDisciplines})`, hours: disciplineHours * complexityMult * shutdownMult, pct: (disciplineHours / rawWeekly) * 100, color: '#06b6d4' },
        subcontractors: { label: `Subcontractors (${detSubcontractors})`, hours: subconHours * complexityMult * shutdownMult, pct: (subconHours / rawWeekly) * 100, color: '#f59e0b' },
        manhours: { label: 'Manhour scale', hours: manhourScale * complexityMult * shutdownMult, pct: (manhourScale / rawWeekly) * 100, color: '#10b981' },
        ...(detIsShutdown ? {
          shutdown: { label: 'Shutdown/turnaround premium', hours: shutdownHours * complexityMult * shutdownMult, pct: (shutdownHours / rawWeekly) * 100, color: '#ef4444' },
        } : {}),
      },
    };
  }, [detProjectValue, detDurationWeeks, detComplexity, detAreas, detDisciplines, detSubcontractors, detTotalManhours, detIsShutdown]);

  const handlePrint = useCallback(() => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>Scheduling Estimate</title>
      <style>body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #111; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }</style>
      </head><body>${content.innerHTML}</body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  }, []);

  const currentResults = activeMethod === 'quick' ? quickResults : detailedResults;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f0f2f5' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
        padding: '20px 28px', flexShrink: 0, borderBottom: '3px solid #dc2626',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.4rem', color: '#fff', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
              Scheduling Manhour Estimator
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>
              Estimate required scheduling effort using industry-standard methods
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <ActiveViewers
              viewers={viewers}
              isConnected={isConnected}
              connectionState={connectionState}
              maxVisible={4}
              showConnectionStatus={true}
            />
            <button onClick={handlePrint} style={styles.headerBtn}>
              Print
            </button>
            <button onClick={() => setShowHelp(true)} style={{ ...styles.headerBtn, backgroundColor: 'rgba(220,38,38,0.15)', color: '#fca5a5', borderColor: 'rgba(220,38,38,0.3)' }}>
              Help
            </button>
          </div>
        </div>

        {/* Method Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginTop: '16px' }}>
          {[
            { id: 'quick', label: 'Quick Estimate', sub: 'Rule of Thumb' },
            { id: 'detailed', label: 'Detailed Estimate', sub: 'Complexity-Based' },
          ].map(tab => {
            const isActive = activeMethod === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveMethod(tab.id)}
                style={{
                  padding: '10px 20px', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer',
                  backgroundColor: isActive ? '#f0f2f5' : 'rgba(255,255,255,0.08)',
                  color: isActive ? '#111827' : '#94a3b8',
                  fontWeight: isActive ? '600' : '400', fontSize: '0.85rem',
                  transition: 'all 0.2s',
                }}
              >
                <div>{tab.label}</div>
                <div style={{ fontSize: '0.65rem', opacity: 0.7, marginTop: '2px' }}>{tab.sub}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div ref={printRef} style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', maxWidth: '1200px' }}>

          {/* Left: Inputs */}
          <div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>
                {activeMethod === 'quick' ? 'Project Parameters' : 'Project Details'}
              </h3>

              {activeMethod === 'quick' ? (
                <>
                  <InputField label="Project Value" value={quickProjectValue} onChange={setQuickProjectValue} suffix="$ Million" step={0.5} tooltip="total contract value" />
                  <InputField label="Project Duration" value={quickDurationWeeks} onChange={setQuickDurationWeeks} suffix="weeks" tooltip="expected duration" />
                </>
              ) : (
                <>
                  <InputField label="Project Value" value={detProjectValue} onChange={setDetProjectValue} suffix="$ Million" step={0.5} tooltip="total contract value" />
                  <InputField label="Project Duration" value={detDurationWeeks} onChange={setDetDurationWeeks} suffix="weeks" tooltip="expected duration" />
                  <ComplexitySelector value={detComplexity} onChange={setDetComplexity} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <InputField label="Number of Areas" value={detAreas} onChange={setDetAreas} tooltip="work zones" />
                    <InputField label="Disciplines" value={detDisciplines} onChange={setDetDisciplines} tooltip="trades involved" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <InputField label="Subcontractors" value={detSubcontractors} onChange={setDetSubcontractors} />
                    <InputField label="Total Manhours" value={detTotalManhours} onChange={setDetTotalManhours} step={1000} tooltip="project total" />
                  </div>

                  {/* Shutdown / Turnaround Toggle */}
                  <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                    <button
                      onClick={() => setDetIsShutdown(!detIsShutdown)}
                      style={{
                        width: '100%', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                        border: detIsShutdown ? '2px solid #ef4444' : '1px solid #d1d5db',
                        backgroundColor: detIsShutdown ? '#fef2f2' : '#fff',
                        display: 'flex', alignItems: 'center', gap: '12px',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div style={{
                        width: '40px', height: '22px', borderRadius: '11px', position: 'relative',
                        backgroundColor: detIsShutdown ? '#ef4444' : '#d1d5db', transition: 'background-color 0.2s',
                        flexShrink: 0,
                      }}>
                        <div style={{
                          width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#fff',
                          position: 'absolute', top: '2px', left: detIsShutdown ? '20px' : '2px',
                          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: '600', color: detIsShutdown ? '#dc2626' : '#374151' }}>
                          Shutdown / Turnaround Project
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>
                          Compressed timeline, 6-7 day weeks, intensive coordination (1.5x multiplier)
                        </div>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Quick reference table */}
            {activeMethod === 'quick' && (
              <div style={{ ...styles.card, marginTop: '16px' }}>
                <h3 style={styles.cardTitle}>Quick Reference</h3>
                {quickResults.isMinApplied && (
                  <div style={{ padding: '8px 12px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', marginBottom: '12px', fontSize: '0.75rem', color: '#92400e' }}>
                    Minimum of <strong>{quickResults.minimum} hrs/week</strong> applied (calculated {formatNumber(quickResults.calculated)} hrs/week is below tier minimum)
                  </div>
                )}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={styles.refTh}>Project Value</th>
                      <th style={styles.refTh}>Calculated</th>
                      <th style={styles.refTh}>Minimum</th>
                      <th style={styles.refTh}>Applied</th>
                      <th style={styles.refTh}>Site Presence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[5, 10, 15, 20, 25, 30, 40, 50].map(v => {
                      const tier = getSitePresenceTier(v);
                      const calc = v * HOURS_PER_MILLION;
                      const min = getMinimumHours(v);
                      const applied = Math.max(calc, min);
                      const isMinUsed = calc < min;
                      const isHighlighted = Math.abs(v - quickProjectValue) < 2.5;
                      return (
                        <tr key={v} style={{ backgroundColor: isHighlighted ? '#fef2f2' : 'transparent', borderBottom: '1px solid #f3f4f6', transition: 'background-color 0.3s' }}>
                          <td style={styles.refTd}>${v}M</td>
                          <td style={{ ...styles.refTd, color: isMinUsed ? '#9ca3af' : '#374151', textDecoration: isMinUsed ? 'line-through' : 'none' }}>{formatNumber(calc)}</td>
                          <td style={{ ...styles.refTd, color: '#6b7280' }}>{min}</td>
                          <td style={{ ...styles.refTd, fontWeight: '700', color: '#111827' }}>{formatNumber(applied)}</td>
                          <td style={{ ...styles.refTd, color: tier.color, fontWeight: '600' }}>{tier.label}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Right: Results */}
          <div>
            {/* Summary Cards */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <ResultCard label="Weekly Hours" value={formatNumber(currentResults.weeklyHours)} unit="hrs/wk" accent="#dc2626" large />
              <ResultCard label="Total Hours" value={formatNumber(currentResults.totalHours, 0)} unit="hrs" subtitle={`over ${activeMethod === 'quick' ? quickDurationWeeks : detDurationWeeks} weeks`} accent="#3b82f6" large />
            </div>

            {/* Site Presence */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Site Presence Recommendation</h3>
              <SitePresenceGauge days={currentResults.tier.days} label={currentResults.tier.label} color={currentResults.tier.color} />
              <div style={{ marginTop: '16px', padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.6 }}>
                  Based on a <strong>${activeMethod === 'quick' ? quickProjectValue : detProjectValue}M</strong> project,
                  the scheduler should be on-site a minimum of <strong style={{ color: currentResults.tier.color }}>{currentResults.tier.label}</strong>.
                </div>
              </div>
            </div>

            {/* Detailed Breakdown */}
            {activeMethod === 'detailed' && (
              <div style={{ ...styles.card, marginTop: '16px' }}>
                <h3 style={styles.cardTitle}>
                  Weekly Hours Breakdown
                  <span style={{ fontSize: '0.7rem', fontWeight: '400', color: '#9ca3af', marginLeft: '8px' }}>
                    ({detailedResults.complexityMult}x complexity{detIsShutdown ? ` × ${detailedResults.shutdownMult}x shutdown` : ''})
                  </span>
                </h3>
                {detailedResults.isMinApplied && (
                  <div style={{ padding: '8px 12px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', marginBottom: '12px', fontSize: '0.75rem', color: '#92400e' }}>
                    Minimum weekly hours applied for this project value tier
                  </div>
                )}
                {Object.values(detailedResults.breakdown).map(b => (
                  <BreakdownRow key={b.label} {...b} />
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', marginTop: '4px', borderTop: '2px solid #e5e7eb' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#111827' }}>Total</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#dc2626' }}>{formatNumber(detailedResults.weeklyHours)} hrs/week</span>
                </div>
              </div>
            )}

            {/* Comparison (both methods) */}
            <div style={{ ...styles.card, marginTop: '16px', background: 'linear-gradient(135deg, #fafafa, #f5f5f5)' }}>
              <h3 style={styles.cardTitle}>Method Comparison</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#fff', borderRadius: '10px', border: activeMethod === 'quick' ? '2px solid #dc2626' : '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick Estimate</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#111827', marginTop: '4px' }}>
                    {formatNumber(quickResults.weeklyHours)} <span style={{ fontSize: '0.7em', color: '#6b7280' }}>hrs/wk</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{formatNumber(quickResults.totalHours, 0)} total</div>
                </div>
                <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#fff', borderRadius: '10px', border: activeMethod === 'detailed' ? '2px solid #dc2626' : '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Detailed Estimate</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#111827', marginTop: '4px' }}>
                    {formatNumber(detailedResults.weeklyHours)} <span style={{ fontSize: '0.7em', color: '#6b7280' }}>hrs/wk</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{formatNumber(detailedResults.totalHours, 0)} total</div>
                </div>
              </div>
              {Math.abs(quickResults.weeklyHours - detailedResults.weeklyHours) > 0.1 && (
                <div style={{ marginTop: '12px', textAlign: 'center', fontSize: '0.75rem', color: '#6b7280' }}>
                  Variance: <strong style={{ color: Math.abs(quickResults.weeklyHours - detailedResults.weeklyHours) > quickResults.weeklyHours * 0.3 ? '#ef4444' : '#10b981' }}>
                    {formatNumber(Math.abs(quickResults.weeklyHours - detailedResults.weeklyHours))} hrs/wk
                  </strong>
                  {' '}({formatNumber(Math.abs(quickResults.weeklyHours - detailedResults.weeklyHours) / Math.max(quickResults.weeklyHours, 0.01) * 100, 0)}% difference)
                </div>
              )}
            </div>

            {/* Monthly cost estimate */}
            <div style={{ ...styles.card, marginTop: '16px' }}>
              <h3 style={styles.cardTitle}>Cost Indication</h3>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '12px' }}>
                Approximate monthly scheduling cost at typical loaded rates:
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                {[
                  { rate: 85, label: 'Junior ($85/hr)' },
                  { rate: 110, label: 'Intermediate ($110/hr)' },
                  { rate: 140, label: 'Senior ($140/hr)' },
                ].map(r => (
                  <div key={r.rate} style={{ textAlign: 'center', padding: '12px 8px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '4px' }}>{r.label}</div>
                    <div style={{ fontSize: '1rem', fontWeight: '700', color: '#111827' }}>
                      ${formatNumber(currentResults.weeklyHours * r.rate * 4.33, 0)}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af' }}>/month</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = {
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '20px 24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardTitle: {
    margin: '0 0 16px', fontSize: '0.9rem', fontWeight: '600', color: '#111827',
    paddingBottom: '10px', borderBottom: '1px solid #f3f4f6',
  },
  headerBtn: {
    padding: '8px 16px', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px',
    cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', backgroundColor: 'rgba(255,255,255,0.08)', color: '#cbd5e1',
    transition: 'all 0.2s',
  },
  refTh: { textAlign: 'left', padding: '8px 10px', color: '#6b7280', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  refTd: { padding: '8px 10px', color: '#374151' },
  modalOverlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
  },
  modalContent: {
    backgroundColor: '#fff', borderRadius: '16px', width: '720px', maxWidth: '90vw',
    maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 24px 12px', borderBottom: 'none',
  },
  modalClose: {
    background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#9ca3af',
    padding: '4px 8px', borderRadius: '4px', lineHeight: 1,
  },
  helpH3: { margin: '0 0 12px', fontSize: '1rem', color: '#111827', fontWeight: '600' },
  formulaBox: {
    padding: '14px 20px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca',
    fontSize: '0.9rem', color: '#991b1b', textAlign: 'center', margin: '12px 0',
  },
  helpTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', marginTop: '12px' },
  helpTh: { textAlign: 'left', padding: '8px 10px', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600', fontSize: '0.75rem' },
  helpTd: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', color: '#4b5563' },
};

export default SchedulingEstimatorPage;
